(() => {
  const SELECTORS = {
    carousel: '[data-kih-carousel]',
    track: '[data-kih-carousel-track]',
    slide: '[data-kih-slide]',
    previous: '[data-kih-carousel-prev]',
    next: '[data-kih-carousel-next]',
    dotGroup: '.kih-waitlist__carousel-dots',
    dot: '[data-kih-carousel-dot]',
    form: '.kih-waitlist__form',
    submit: '.kih-waitlist__submit',
    status: '.kih-waitlist__status',
    errors: '.kih-waitlist__errors',
    firstName: '[data-kih-first-name]',
    success: '[data-kih-waitlist-success]',
    successHeading: '[data-kih-success-heading]',
    inPageLink: '.kih-waitlist a[href^="#"]',
    embedField:
      '.klaviyo-form-Y2Fnpz input:not([type="hidden"]), .klaviyo-form-Y2Fnpz select, .klaviyo-form-Y2Fnpz textarea',
    passwordModal: 'password-modal',
    countdown: '[data-kih-countdown]',
    countdownDays: '[data-kih-countdown-days]',
    countdownHours: '[data-kih-countdown-hours]',
    countdownMinutes: '[data-kih-countdown-minutes]',
    countdownSeconds: '[data-kih-countdown-seconds]',
    countdownExpired: '[data-kih-countdown-expired]',
    embed: '[data-kih-embed]',
    embedKlaviyo: '.klaviyo-form-Y2Fnpz',
    embedSkeleton: '[data-kih-embed-skeleton]',
    embedStatus: '[data-kih-embed-status]',
    fallbackForm: '[data-kih-wl-fallback]',
    fallbackFields: '[data-kih-wlfb-fields]',
    fallbackSubmit: '[data-kih-wlfb-submit]',
    fallbackSuccess: '[data-kih-wlfb-success]',
    fallbackFormError: '[data-kih-wlfb-formerror]',
    fallbackFirst: '[data-kih-wlfb-first]',
    fallbackLast: '[data-kih-wlfb-last]',
    fallbackEmail: '[data-kih-wlfb-email]',
    fallbackRegion: '[data-kih-wlfb-region]'
  };

  /*
    Klaviyo's public client subscriptions endpoint. Same endpoint, same revision and
    the same list the footer newsletter uses, so a profile created here is identical
    to one created there apart from its signup_source property.
  */
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_REVISION = '2025-04-15';
  const FALLBACK_SIGNUP_SOURCE = 'waitlist_page_fallback';
  const FALLBACK_TIMEOUT_DEFAULT = 3500;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const storageKey = 'kihWaitlistFirstName';

  /*
    Every setup function is registered through initOnce(), which stamps a data-*
    flag on the element it initialised. Re-running the whole script, or re-scanning
    after a Shopify theme-editor section reload, can therefore never attach a second
    set of listeners or start a second autoplay interval on the same element.
    Teardowns are kept so detached elements release their timers and their
    document-level listeners when the editor swaps a section out.
  */
  const teardowns = new Map();

  const initOnce = (root, selector, flag, setup) => {
    const scope = root && root.querySelectorAll ? root : document;
    const matches = Array.from(scope.querySelectorAll(selector));

    if (scope.matches && scope.matches(selector)) matches.unshift(scope);

    matches.forEach((element) => {
      if (element.dataset[flag] === 'true') return;
      element.dataset[flag] = 'true';

      const teardown = setup(element);
      if (typeof teardown === 'function') teardowns.set(element, teardown);
    });
  };

  const cleanupDetached = () => {
    teardowns.forEach((teardown, element) => {
      if (element.isConnected) return;
      teardowns.delete(element);
      try {
        teardown();
      } catch (error) {
        // A failed teardown must never block the rest of the page.
      }
    });
  };

  const setupCarousel = (carousel) => {
    const track = carousel.querySelector(SELECTORS.track);
    const slides = Array.from(carousel.querySelectorAll(SELECTORS.slide));
    const dots = Array.from(carousel.querySelectorAll(SELECTORS.dot));
    const previous = carousel.querySelector(SELECTORS.previous);
    const next = carousel.querySelector(SELECTORS.next);

    if (!track || slides.length < 2 || !previous || !next) return;

    const dotGroup = carousel.querySelector(SELECTORS.dotGroup);
    if (dotGroup && !dotGroup.hasAttribute('role')) dotGroup.setAttribute('role', 'group');

    let activeIndex = 0;
    let timer = null;
    let scrollFrame = null;
    let paused = false;

    const updateState = (index) => {
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        slide.setAttribute('aria-hidden', slideIndex === activeIndex ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        if (dotIndex === activeIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    };

    const goTo = (index, smooth = true) => {
      const resolvedIndex = (index + slides.length) % slides.length;
      const behavior = smooth && !reducedMotion.matches ? 'smooth' : 'auto';

      /*
        The dot follows the track, it does not lead it. On a smooth scroll the scroll
        listener below owns the state, so aria-current only moves to the target slide
        once the track has actually arrived there. Instant jumps (reduced motion,
        resize) still update synchronously: they land in the same frame, and a jump
        that is already at its destination fires no scroll event at all.
      */
      if (behavior === 'auto') updateState(resolvedIndex);

      track.scrollTo({
        left: slides[resolvedIndex].offsetLeft,
        behavior
      });
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (paused || reducedMotion.matches || document.hidden) return;
      timer = window.setInterval(() => goTo(activeIndex + 1), 5200);
    };

    const useControl = (index) => {
      goTo(index);
      start();
    };

    const onPreviousClick = () => useControl(activeIndex - 1);
    const onNextClick = () => useControl(activeIndex + 1);

    previous.addEventListener('click', onPreviousClick);
    next.addEventListener('click', onNextClick);

    dots.forEach((dot) => {
      dot.addEventListener('click', () => useControl(Number(dot.dataset.kihCarouselDot)));
    });

    // Keyboard operation for the carousel region itself, in addition to the buttons.
    const onKeydown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      let handled = true;
      if (event.key === 'ArrowLeft') useControl(activeIndex - 1);
      else if (event.key === 'ArrowRight') useControl(activeIndex + 1);
      else if (event.key === 'Home') useControl(0);
      else if (event.key === 'End') useControl(slides.length - 1);
      else handled = false;

      if (handled) event.preventDefault();
    };

    carousel.addEventListener('keydown', onKeydown);

    const onScroll = () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const closestIndex = slides.reduce((closest, slide, index) => {
          const currentDistance = Math.abs(slide.offsetLeft - track.scrollLeft);
          const closestDistance = Math.abs(slides[closest].offsetLeft - track.scrollLeft);
          return currentDistance < closestDistance ? index : closest;
        }, 0);
        updateState(closestIndex);
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });

    carousel.addEventListener('mouseenter', () => {
      paused = true;
      stop();
    });

    carousel.addEventListener('mouseleave', () => {
      paused = false;
      start();
    });

    carousel.addEventListener('focusin', () => {
      paused = true;
      stop();
    });

    carousel.addEventListener('focusout', (event) => {
      if (carousel.contains(event.relatedTarget)) return;
      paused = false;
      start();
    });

    // Autoplay must not keep running while the tab is in the background.
    const onVisibilityChange = () => start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    const onMotionChange = () => start();
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onMotionChange);
    }

    const onResize = () => goTo(activeIndex, false);
    window.addEventListener('resize', onResize, { passive: true });

    updateState(0);
    start();

    return () => {
      stop();
      window.cancelAnimationFrame(scrollFrame);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (typeof reducedMotion.removeEventListener === 'function') {
        reducedMotion.removeEventListener('change', onMotionChange);
      }
      window.removeEventListener('resize', onResize);
    };
  };

  const setupWaitlistForm = (form) => {
    const submit = form.querySelector(SELECTORS.submit);

    /*
      Status text lives next to the submit button, not in place of it. The button is
      never hidden, disabled or removed while the request is in flight - it keeps its
      label and stays in the accessibility tree, and repeat submits are swallowed
      below instead.
    */
    let status = form.querySelector(SELECTORS.status);
    if (!status && submit && submit.parentNode) {
      status = document.createElement('p');
      status.className = 'kih-waitlist__status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      submit.insertAdjacentElement('afterend', status);
    }

    let submitting = false;

    const clearBusyState = () => {
      submitting = false;
      if (submit) {
        delete submit.dataset.kihLoading;
        submit.removeAttribute('aria-disabled');
        submit.removeAttribute('aria-busy');
      }
      if (status) status.textContent = '';
    };

    const onSubmit = (event) => {
      if (submitting) {
        event.preventDefault();
        return;
      }

      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

      submitting = true;

      if (submit) {
        submit.dataset.kihLoading = 'true';
        submit.setAttribute('aria-disabled', 'true');
        submit.setAttribute('aria-busy', 'true');
      }

      if (status) status.textContent = 'Joining the Reminder List…';

      const firstName = form.querySelector(SELECTORS.firstName)?.value.trim();
      if (!firstName) return;

      try {
        window.sessionStorage.setItem(storageKey, firstName);
      } catch (error) {
        // The form remains fully functional when storage is unavailable.
      }
    };

    const onSubmitClick = (event) => {
      if (submitting) event.preventDefault();
    };

    form.addEventListener('submit', onSubmit);
    if (submit) submit.addEventListener('click', onSubmitClick);

    // Restores the button after a back/forward navigation out of the bfcache.
    window.addEventListener('pageshow', clearBusyState);

    return () => window.removeEventListener('pageshow', clearBusyState);
  };

  const personalizeSuccess = (success) => {
    const heading = success.querySelector(SELECTORS.successHeading);
    if (!heading) return;

    try {
      const firstName = window.sessionStorage.getItem(storageKey);
      if (firstName) {
        heading.textContent = `${firstName}, first access is headed to your inbox.`;
        window.sessionStorage.removeItem(storageKey);
      }
    } catch (error) {
      // The default success message is already present.
    }

    window.requestAnimationFrame(() => success.focus({ preventScroll: true }));
  };

  /*
    After a failed submit the page re-renders and the browser drops focus to <body>.
    Move it onto the error summary so keyboard and screen-reader users are told what
    happened instead of being silently returned to the top of the document.
  */
  const focusErrorSummary = (errors) => {
    if (document.querySelector(SELECTORS.success)) return;
    if (!errors.hasAttribute('tabindex')) errors.setAttribute('tabindex', '-1');
    window.requestAnimationFrame(() => errors.focus());
  };

  /*
    The Klaviyo embed is injected asynchronously by klaviyo.js, so its fields do not
    exist when this listener is bound and must be resolved on every click instead.
    Inputs belonging to a step that is not currently shown have no client rects, so
    only a genuinely visible, enabled control is accepted.
  */
  const resolveEmbedField = (region) => {
    const candidates = Array.from(region.querySelectorAll(SELECTORS.embedField));
    return candidates.find((field) => !field.disabled && field.getClientRects().length > 0) || null;
  };

  /*
    The closing "Join the Reminder List" link must move keyboard focus into the form
    region, not merely scroll the page to it.

    This takes the navigation over instead of racing it. Left to itself the browser
    performs its own fragment navigation after this handler returns, and that resets
    focus to the document - measured on the live page, it silently undid a focus()
    made from here, whether called directly or from requestAnimationFrame. Doing the
    scroll and the focus move ourselves, in one order, is the only way the field
    reliably ends up focused. The hash is still written so the URL keeps reflecting
    the section and the link stays a real, copyable in-page link.
  */
  const setupInPageLink = (link) => {
    const onClick = (event) => {
      const href = link.getAttribute('href') || '';
      const id = href.slice(1);
      if (!id) return;

      const target = document.getElementById(id);
      if (!target) return;

      event.preventDefault();

      // Resolved at click time: the embed is injected asynchronously by klaviyo.js.
      const field = resolveEmbedField(target);
      if (!field && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');

      target.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start'
      });

      try {
        window.history.replaceState(null, '', href);
      } catch (error) {
        // A blocked history write must never stop the focus move below.
      }

      (field || target).focus({ preventScroll: true });
    };

    link.addEventListener('click', onClick);
  };

  const setupPasswordModal = (modal) => {
    const details = modal.querySelector('details');
    const summary = modal.querySelector('summary');
    if (!details || !summary) return;

    /*
      Dawn's own <password-modal> / DetailsModal component already closes on Escape and
      returns focus to the summary. When it is registered we deliberately do not add a
      second Escape handler or a second focus() call, so we never double-close the
      dialog or fight its focus trap. We only supply that behaviour ourselves if the
      component is missing or failed to upgrade.
    */
    const dawnHandlesModal = !!(window.customElements && window.customElements.get('password-modal'));

    const returnFocus = () => {
      details.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      summary.focus();
    };

    if (!dawnHandlesModal) {
      details.addEventListener('keyup', (event) => {
        const key = event.key || event.code || '';
        if (key.toUpperCase().indexOf('ESC') === 0) returnFocus();
      });

      const closeButton = modal.querySelector('.modal__close-button');
      if (closeButton) closeButton.addEventListener('click', returnFocus);
    }

    /*
      Safety net regardless of which component closed the dialog: if it ends up closed
      while focus has fallen back to <body>, put focus back on the opener so keyboard
      users are never stranded at the top of the document.
    */
    const observer = new MutationObserver(() => {
      if (details.hasAttribute('open')) return;
      const active = document.activeElement;
      if (!active || active === document.body) summary.focus();
    });

    observer.observe(details, { attributes: true, attributeFilter: ['open'] });

    return () => observer.disconnect();
  };

  /*
    EMBED RESILIENCE.

    The Klaviyo embed is injected by klaviyo.js, which is loaded sitewide by the app
    embed. When that script is blocked, throttled or slow, .klaviyo-form-Y2Fnpz stays
    an empty div and the waitlist page loses its only conversion path with no visible
    error. Three states are managed here:

      waiting  - the placeholder holds the card at the embed's height.
      fallback - nothing mounted before the timeout, so the native form is revealed.
      embed    - Klaviyo mounted; the placeholder and the native form step aside.

    The observer stays connected after the fallback is revealed so a late mount is
    still caught. It will NOT pull the native form out from under a visitor who has
    already started typing in it - losing typed input to a late script is worse than
    showing two ways to join.
  */
  const setupEmbedFallback = (embed) => {
    const klaviyo = embed.querySelector(SELECTORS.embedKlaviyo);
    const fallback = embed.querySelector(SELECTORS.fallbackForm);
    const skeleton = embed.querySelector(SELECTORS.embedSkeleton);
    const status = embed.querySelector(SELECTORS.embedStatus);

    if (!klaviyo || !fallback) return;

    const parsedTimeout = Number.parseInt(embed.dataset.kihEmbedTimeout, 10);
    const timeout = Number.isFinite(parsedTimeout) ? parsedTimeout : FALLBACK_TIMEOUT_DEFAULT;

    let timer = null;
    let observer = null;
    let state = 'waiting';

    const embedMounted = () => {
      if (!klaviyo.childElementCount) return false;
      // A wrapper with no rendered height is Klaviyo's known half-mounted state.
      return klaviyo.offsetHeight > 0 || !!klaviyo.querySelector('form, input');
    };

    const fallbackEngaged = () => {
      if (fallback.dataset.kihEngaged === 'true') return true;
      if (fallback.contains(document.activeElement)) return true;
      return Array.prototype.some.call(
        fallback.querySelectorAll('input, select'),
        (field) => (field.value || '').trim() !== ''
      );
    };

    const setStatus = (text) => {
      if (status) status.textContent = text;
    };

    const showFallback = () => {
      if (state === 'embed' || state === 'fallback') return;
      state = 'fallback';
      if (skeleton) skeleton.hidden = true;
      fallback.hidden = false;
      setStatus('Signup form ready.');
    };

    const showEmbed = () => {
      if (state === 'embed') return;
      state = 'embed';
      window.clearTimeout(timer);
      if (skeleton) skeleton.hidden = true;
      if (!fallbackEngaged()) fallback.hidden = true;
      setStatus('');
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    const onEngage = () => {
      fallback.dataset.kihEngaged = 'true';
    };

    fallback.addEventListener('input', onEngage);
    fallback.addEventListener('focusin', onEngage);

    if (embedMounted()) {
      showEmbed();
      return () => {
        fallback.removeEventListener('input', onEngage);
        fallback.removeEventListener('focusin', onEngage);
      };
    }

    if (typeof window.MutationObserver === 'function') {
      observer = new MutationObserver(() => {
        if (embedMounted()) showEmbed();
      });
      observer.observe(klaviyo, { childList: true, subtree: true });
    }

    timer = window.setTimeout(() => {
      if (embedMounted()) showEmbed();
      else showFallback();
    }, timeout);

    return () => {
      window.clearTimeout(timer);
      if (observer) observer.disconnect();
      fallback.removeEventListener('input', onEngage);
      fallback.removeEventListener('focusin', onEngage);
    };
  };

  /*
    NATIVE FALLBACK SUBMIT.

    Deliberately the same validation, the same payload shape and the same two-latch
    double-submit guard as the footer newsletter in sections/kih-footer.liquid, so
    there is one signup behaviour on this storefront rather than two. The only
    difference is properties.signup_source, which lets a Klaviyo segment measure how
    often the embed failed.
  */
  const setupFallbackSubmit = (form) => {
    const submit = form.querySelector(SELECTORS.fallbackSubmit);
    const fieldsWrap = form.querySelector(SELECTORS.fallbackFields);
    const successBox = form.querySelector(SELECTORS.fallbackSuccess);
    const formError = form.querySelector(SELECTORS.fallbackFormError);
    const firstEl = form.querySelector(SELECTORS.fallbackFirst);
    const lastEl = form.querySelector(SELECTORS.fallbackLast);
    const emailEl = form.querySelector(SELECTORS.fallbackEmail);
    const regionEl = form.querySelector(SELECTORS.fallbackRegion);
    const company = form.getAttribute('data-klaviyo-company');
    const listId = form.getAttribute('data-klaviyo-list');

    if (!submit || !emailEl || !company || !listId) return;

    // `submitting` blocks a second click while a request is in flight; `done` blocks
    // every further submit once Klaviyo has accepted the profile.
    let submitting = false;
    let done = false;

    const errorBoxFor = (field) =>
      field.parentNode ? field.parentNode.querySelector('[data-kih-wlfb-error]') : null;

    const showFieldError = (field, message) => {
      const box = errorBoxFor(field);
      if (box) {
        box.textContent = message;
        box.hidden = false;
      }
      field.setAttribute('aria-invalid', 'true');
    };

    const clearFieldError = (field) => {
      const box = errorBoxFor(field);
      if (box) {
        box.textContent = '';
        box.hidden = true;
      }
      field.removeAttribute('aria-invalid');
    };

    const validateField = (field) => {
      clearFieldError(field);
      const value = (field.value || '').trim();

      if (!value) {
        showFieldError(
          field,
          field.tagName === 'SELECT' ? 'Please choose your region.' : 'This field is required.'
        );
        return false;
      }

      if (field.type === 'email') {
        const at = value.indexOf('@');
        const dot = value.lastIndexOf('.');
        const spaced = value.indexOf(' ') > -1;
        if (spaced || at < 1 || dot < at + 2 || dot > value.length - 3) {
          showFieldError(field, 'Enter a valid email address, for example name@example.com.');
          return false;
        }
      }

      return true;
    };

    const required = Array.prototype.slice.call(form.querySelectorAll('[required]'));
    required.forEach((field) => {
      field.addEventListener('blur', () => validateField(field));
      field.addEventListener('input', () => clearFieldError(field));
      field.addEventListener('change', () => clearFieldError(field));
    });

    const resetSubmit = () => {
      submit.removeAttribute('aria-disabled');
      submit.removeAttribute('aria-busy');
      submit.textContent = 'Join the Reminder List';
    };

    const onSubmit = (event) => {
      event.preventDefault();
      if (submitting || done) return;

      let firstInvalid = null;
      required.forEach((field) => {
        if (!validateField(field) && !firstInvalid) firstInvalid = field;
      });
      if (firstInvalid) {
        firstInvalid.focus();
        return;
      }

      if (formError) {
        formError.hidden = true;
        formError.textContent = '';
      }

      submitting = true;
      submit.setAttribute('aria-disabled', 'true');
      submit.setAttribute('aria-busy', 'true');
      submit.textContent = 'Joining…';

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: (emailEl.value || '').trim(),
                  first_name: firstEl ? (firstEl.value || '').trim() : '',
                  last_name: lastEl ? (lastEl.value || '').trim() : '',
                  properties: {
                    signup_source: FALLBACK_SIGNUP_SOURCE,
                    region_group: regionEl ? regionEl.value : ''
                  },
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } }
                  }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: listId } }
          }
        }
      };

      window
        .fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(company), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        })
        .then((response) => {
          // The client endpoint answers 202 Accepted with an empty body.
          if (!response.ok && response.status !== 202) {
            throw new Error('Klaviyo responded ' + response.status);
          }
          done = true;
          if (fieldsWrap) fieldsWrap.hidden = true;
          submit.hidden = true;
          if (successBox) {
            successBox.hidden = false;
            successBox.focus({ preventScroll: true });
          }
        })
        .catch(() => {
          if (formError) {
            formError.textContent =
              'We could not add you just now. Please try again in a moment, or email info@keepinithalalbrand.com and we will add you by hand.';
            formError.hidden = false;
          }
        })
        .then(() => {
          submitting = false;
          if (!done) resetSubmit();
        });
    };

    const onSubmitClick = (event) => {
      if (submitting || done) event.preventDefault();
    };

    form.addEventListener('submit', onSubmit);
    submit.addEventListener('click', onSubmitClick);

    // Restores the button after a back/forward navigation out of the bfcache.
    const onPageShow = () => {
      if (done) return;
      submitting = false;
      resetSubmit();
    };
    window.addEventListener('pageshow', onPageShow);

    return () => window.removeEventListener('pageshow', onPageShow);
  };

  const setupCountdown = (countdown) => {
    const deadline = Date.parse(countdown.dataset.kihCountdownEnd || '');
    const days = countdown.querySelector(SELECTORS.countdownDays);
    const hours = countdown.querySelector(SELECTORS.countdownHours);
    const minutes = countdown.querySelector(SELECTORS.countdownMinutes);
    const seconds = countdown.querySelector(SELECTORS.countdownSeconds);
    const expired = countdown.querySelector(SELECTORS.countdownExpired);

    if (!Number.isFinite(deadline) || !days || !hours || !minutes || !seconds) return;

    let timer = null;
    const pad = (value) => String(value).padStart(2, '0');

    const update = () => {
      const remaining = Math.max(0, deadline - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);

      days.textContent = pad(Math.floor(totalSeconds / 86400));
      hours.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
      minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      seconds.textContent = pad(totalSeconds % 60);

      if (remaining > 0) return;

      window.clearInterval(timer);
      countdown.dataset.kihCountdownComplete = 'true';
      if (expired) expired.hidden = false;
    };

    update();
    timer = window.setInterval(update, 1000);

    return () => window.clearInterval(timer);
  };

  const boot = (root) => {
    const scope = root || document;

    cleanupDetached();

    initOnce(scope, SELECTORS.carousel, 'kihCarouselReady', setupCarousel);
    initOnce(scope, SELECTORS.form, 'kihFormReady', setupWaitlistForm);
    initOnce(scope, SELECTORS.success, 'kihSuccessReady', personalizeSuccess);
    initOnce(scope, SELECTORS.errors, 'kihErrorsReady', focusErrorSummary);
    initOnce(scope, SELECTORS.inPageLink, 'kihLinkReady', setupInPageLink);
    initOnce(document, SELECTORS.passwordModal, 'kihModalReady', setupPasswordModal);
    initOnce(scope, SELECTORS.countdown, 'kihCountdownReady', setupCountdown);
    initOnce(scope, SELECTORS.fallbackForm, 'kihFallbackFormReady', setupFallbackSubmit);
    initOnce(scope, SELECTORS.embed, 'kihEmbedReady', setupEmbedFallback);
  };

  // A second execution of this file re-scans instead of re-binding everything.
  if (typeof window.kihWaitlistBoot === 'function') {
    window.kihWaitlistBoot();
    return;
  }

  window.kihWaitlistBoot = boot;

  // Shopify theme editor: sections are swapped in and out of the live DOM.
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', () => cleanupDetached());

  boot();
})();
